import { NO_CURRENCY } from "../../billing/utils/money";

/** Default billing currency used when the organization record carries none yet. */
export const DEFAULT_CURRENCY = "USD";

/**
 * Billing currencies offered in settings: the ten most traded world currencies,
 * the pre-existing PLN/UAH, and a "no currency" option (ISO 4217 "XXX") so
 * instructors whose currency isn't listed can show plain numbers instead.
 * Keep in sync with SUPPORTED_CURRENCIES in billing/utils/money.ts.
 */
export const CURRENCY_OPTIONS: { code: string; label: string }[] = [
  { code: "USD", label: "USD — US Dollar" },
  { code: "EUR", label: "EUR — Euro" },
  { code: "GBP", label: "GBP — British Pound" },
  { code: "JPY", label: "JPY — Japanese Yen" },
  { code: "CNY", label: "CNY — Chinese Yuan" },
  { code: "AUD", label: "AUD — Australian Dollar" },
  { code: "CAD", label: "CAD — Canadian Dollar" },
  { code: "CHF", label: "CHF — Swiss Franc" },
  { code: "HKD", label: "HKD — Hong Kong Dollar" },
  { code: "SGD", label: "SGD — Singapore Dollar" },
  { code: "PLN", label: "PLN — Polish Złoty" },
  { code: "UAH", label: "UAH — Ukrainian Hryvnia" },
  { code: NO_CURRENCY, label: "No currency — plain numbers" },
];

/**
 * Currency display name in the UI language via Intl.DisplayNames, falling back
 * to the curated English label where the runtime has no localized name.
 * The NO_CURRENCY option is not a real ISO currency — callers translate it.
 */
export const currencyOptionLabel = (code: string, locale: string): string => {
  const fallback =
    CURRENCY_OPTIONS.find((option) => option.code === code)?.label ?? code;
  if (code === NO_CURRENCY) {
    return fallback;
  }
  try {
    const name = new Intl.DisplayNames([locale], { type: "currency" }).of(code);
    return name && name !== code ? `${code} — ${name}` : fallback;
  } catch {
    return fallback;
  }
};
